if (!customElements.get('wls-product-quantity-selector')) {
  class WlsProductQuantitySelector extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        selector: 'wls-product-quantity-selector',
        item: 'wls-product-quantity-selector-item',
        radio: 'wls-product-quantity-selector-radio',
        selected: 'selected',
        checkoutBtn: 'wls-quantity-checkout-btn'
      };

      this.isReinitializing = false;

      // Bind methods
      this.boundHandleItemClick = this.handleItemClick.bind(this);
      this.boundHandleRadioChange = this.handleRadioChange.bind(this);
      this.boundHandleCheckoutClick = this.handleCheckoutClick.bind(this);
      this.boundReInit = this.reInit.bind(this);
    }

    /**
     * DOM elements getter
     */
    get elements() {
      return {
        selector: this.querySelector(`.${this.classes.selector}`),
        items: Array.from(this.querySelectorAll(`.${this.classes.item}`)),
        radios: Array.from(this.querySelectorAll(`.${this.classes.radio}`)),
        checkoutBtn: this.querySelector(`.${this.classes.checkoutBtn}`)
      };
    }

    connectedCallback() {
      this._init();
      this.addEventListener('wls:quantity-selector:reinit', this.boundReInit);
    }

    disconnectedCallback() {
      this._destroy();
      this.removeEventListener('wls:quantity-selector:reinit', this.boundReInit);
    }

    /**
     * Public re-init method
     */
    reInit() {
      if (this.isReinitializing) return;

      this.isReinitializing = true;

      try {
        this._destroy();
        this._init();
      } catch (err) {
        console.error('Error reinitializing quantity selector:', err);
      } finally {
        this.isReinitializing = false;
      }
    }

    /**
     * Initialize component
     */
    _init() {
      const { selector, items, radios, checkoutBtn } = this.elements;

      if (!selector) return;

      // add listeners
      items.forEach(item => item.addEventListener('click', this.boundHandleItemClick));
      radios.forEach(radio => radio.addEventListener('change', this.boundHandleRadioChange));
      if (checkoutBtn) checkoutBtn.addEventListener('click', this.boundHandleCheckoutClick);

      // initial state
      this.updateSelectedState();
    }

    /**
     * Destroy component (cleanup)
     */
    _destroy() {
      const { items, radios, checkoutBtn } = this.elements;

      items.forEach(item => item.removeEventListener('click', this.boundHandleItemClick));
      radios.forEach(radio => radio.removeEventListener('change', this.boundHandleRadioChange));
      if (checkoutBtn) checkoutBtn.removeEventListener('click', this.boundHandleCheckoutClick);
    }

    /**
     * Handle click on quantity item
     */
    handleItemClick(e) {
      if (e.target.type === 'radio') return;

      const radio = e.currentTarget.querySelector(`.${this.classes.radio}`);
      if (!radio) return;

      radio.checked = true;
      this.updateSelectedState();
      this.dispatchChangeEvent(radio);
    }

    /**
     * Handle radio change
     */
    handleRadioChange(e) {
      this.updateSelectedState();
      this.dispatchChangeEvent(e.target);
    }

    /**
     * Update selected state class
     */
    updateSelectedState() {
      const { items } = this.elements;

      items.forEach(item => {
        const radio = item.querySelector(`.${this.classes.radio}`);
        if (radio && radio.checked) {
          item.classList.add(this.classes.selected);
        } else {
          item.classList.remove(this.classes.selected);
        }
      });
    }

    /**
     * Dispatch custom event
     */
    dispatchChangeEvent(radio) {
      const event = new CustomEvent('quantity-selected', {
        detail: {
          value: radio.value,
          quantity: radio.value
        },
        bubbles: true
      });

      this.dispatchEvent(event);
    }

    /**
     * Checkout handler
     */
    async handleCheckoutClick(e) {
      e.preventDefault();
      e.stopPropagation();

      const { checkoutBtn } = this.elements;

      if (!checkoutBtn || checkoutBtn.disabled) return;

      const variantId = checkoutBtn.dataset.variantId;
      const quantity = this.getSelectedValue();

      if (!variantId || !quantity) {
        alert('Please select a quantity option.');
        return;
      }

      // Get discount code from selected item - only if attribute exists and has value
      const selectedRadio = this.elements.radios.find(r => r.checked);
      let discountCode = null;
      
      if (selectedRadio) {
        // Check if data-discount-code attribute exists on radio element
        const radioHasDiscountAttr = selectedRadio.hasAttribute('data-discount-code');
        if (radioHasDiscountAttr) {
          const code = selectedRadio.dataset.discountCode;
          // Only use if it's a non-empty string
          if (code && typeof code === 'string' && code.trim().length > 0) {
            discountCode = code.trim();
          }
        }
        
        // If not found on radio, check parent label element
        if (!discountCode) {
          const parentItem = selectedRadio.closest(`.${this.classes.item}`);
          if (parentItem && parentItem.hasAttribute('data-discount-code')) {
            const code = parentItem.dataset.discountCode;
            if (code && typeof code === 'string' && code.trim().length > 0) {
              discountCode = code.trim();
            }
          }
        }
      }

      // Final validation: discount code must be a valid non-empty string
      const isValidDiscountCode = discountCode !== null && 
                                  discountCode !== undefined &&
                                  typeof discountCode === 'string' && 
                                  discountCode.length > 0;

      try {
        const cartAddUrl = '/cart/add.js';
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', quantity);

        const response = await fetch(cartAddUrl, {
          method: 'POST',
          body: formData
        });

        const contentType = response.headers.get('content-type') || '';
        const isJson = contentType.includes('application/json');

        // Build checkout URL - ONLY add discount if valid code exists
        let checkoutUrl = '/checkout';
        if (isValidDiscountCode) {
          checkoutUrl += `?discount=${encodeURIComponent(discountCode)}`;
        }

        if (!isJson) {
          const text = await response.text();
          console.error('Non-JSON response:', text.substring(0, 500));

          if (response.ok) {
            window.location.href = checkoutUrl;
            return;
          }

          throw new Error('Invalid response from server');
        }

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || errorData.message || 'Failed to add to cart');
        }

        await response.json();
        window.location.href = checkoutUrl;
      } catch (error) {
        console.error('Error adding to cart:', error);
        alert(`Failed to add product to cart: ${error.message || 'Please try again.'}`);
      }
    }

    /**
     * Public get selected value
     */
    getSelectedValue() {
      const { radios } = this.elements;
      const checked = radios.find(r => r.checked);
      return checked ? checked.value : null;
    }

    /**
     * Public set selected value
     */
    setSelectedValue(value) {
      const { radios } = this.elements;
      const radio = radios.find(r => r.value === value);

      if (radio) {
        radio.checked = true;
        this.updateSelectedState();
      }
    }
  }

  customElements.define('wls-product-quantity-selector', WlsProductQuantitySelector);
}
